import React, { useEffect, useState } from 'react';
import clsx from '@strava/ui/clsx';
import {
  breakpoints,
  matchMaxMedia,
  matchMinMedia
} from '@strava/ui/MediaQuery';
import { useStravaErrorLogging } from '@strava/container-context';
import { Picture } from '@strava/shared-ui';
import styles from './TabletImage.module.scss';

interface TabletImageProps {
  className?: string;
}

type Image = {
  file: string;
  resolution: '1x' | '2x';
  format: 'webp' | 'png';
  size: { id: string; minVw: string };
};

export const TabletImage = ({ className }: TabletImageProps) => {
  const { logError } = useStravaErrorLogging();
  const [tabletImages, setTabletImages] = useState<Image[]>([]);

  useEffect(() => {
    (async () => {
      const isTablet = matchMinMedia.screenSm() && matchMaxMedia.screenSmMax();

      if (isTablet) {
        //  webpack comments ensure that the background images are included
        //  with the current chunk, which prevents `active` from making a forbidden
        //  request to cloudfront
        const importImage = async (filename: string) => {
          try {
            return (
              await import(
                /* webpackMode: "eager" */
                /* webpackExports: ["default", "named"] */
                `./assets/${filename}`
              )
            ).default;
          } catch (error) {
            logError(error);
          }
        };

        const resolutions = ['1x', '2x'] as const;
        const formats = ['png', 'webp'] as const;

        resolutions.forEach((resolution) => {
          formats.forEach(async (format) => {
            const file = await importImage(
              `Sign-Up-Page-Tablet@${resolution}.${format}`
            );

            if (file) {
              setTabletImages((prev) => [
                ...prev,
                {
                  format,
                  resolution,
                  file,
                  size: { id: 'sm', minVw: `${breakpoints.screenSm}px` }
                }
              ]);
            }
          });
        });
      }
    })();
  }, [logError]);

  return (
    <div className={clsx(styles.tabletImage, className)}>
      <Picture
        images={tabletImages}
        imgTagFallbackConfig={{ size: 'sm', format: 'png', resolution: '1x' }}
        isLazy={false}
      />
    </div>
  );
};

export default TabletImage;
